import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { FormError } from '~/components/Form/Error/FormError';
import { HelpText } from '~/components/HelpText';
import { Label } from '~/components/Label';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  id: string;
  name: string;
  label?: string;
  helpText?: ReactNode;
  labelVariant?: 'small' | 'large';
  errorMessage?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ id, name, label, helpText, disabled, required, labelVariant = 'small', errorMessage, ...props }, ref) => {
    const getDescriptors = () => (helpText ? `InputHelpText-${id}-help` : undefined);

    return (
      <div>
        <Label id={id} disabled={disabled} variant={labelVariant} required={required}>
          {label}
        </Label>
        <div className={styles.selectWrapper}>
          <input
            id={id}
            name={name}
            disabled={disabled}
            aria-required={required || undefined}
            ref={ref}
            className={`${styles.select} ${errorMessage ? styles.selectError : ''}`}
            aria-describedby={getDescriptors()}
            data-test-id={`input-${id}`}
            {...props}
          />
        </div>
        {helpText && (
          <div className={styles.helpText}>
            <HelpText id={`InputHelpText-${id}`} disabled={disabled}>
              {helpText}
            </HelpText>
          </div>
        )}
        {errorMessage ? <FormError message={errorMessage} /> : null}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
